<script lang="ts">
	import { Modal } from '@Shelly/shelly-design-system/index';
	import VideoPlayer from '../../CameraPlayer/VideoPlayer.svelte';
	import HLSPlayer from '../../CameraPlayer/HLSPlayer.svelte';
	import {
		RecordingMediaType,
		type CloudRecording,
		type CloudRecordingVideo,
	} from '../../types/cloud-recordings';
	import CameraThumbnail from '../../CameraThumbnail.svelte';
	import { dateFormat } from 'src/helpers/date_helper';
	import { buildCloudMediaUrl } from 'src/services/cam-cloud/recordings';
	import { isHlsRecording } from '../../helpers/camera-recordings';
	import ZoomableContainer from '../../CameraPlayer/ZoomableContainer.svelte';

	export let recording: CloudRecording;

	let showFullscreenModal = false;
	let fullscreenMedia: CloudRecording = null;

	let videoUrl = '';

	/**
	 * HLS playlists are served (with JWT auth) and decrypted by HLSPlayer, so
	 * they must NOT be passed through the GCS signer.
	 */
	$: isHls = isHlsRecording(recording);
	$: hlsVideoSrc = (recording as CloudRecordingVideo)?.videoUrl ?? '';

	$: {
		const recordingVideoUrl = (recording as CloudRecordingVideo)?.videoUrl;
		if (recordingVideoUrl && !isHls) {
			buildCloudMediaUrl(recordingVideoUrl).then((url) => (videoUrl = url));
		} else {
			videoUrl = '';
		}
	}

	const handleImageClick = () => {
		fullscreenMedia = recording;
		showFullscreenModal = true;
	};

	const handleCloseModal = () => {
		showFullscreenModal = false;
		fullscreenMedia = null;
	};

	$: formattedDate = fullscreenMedia ? dateFormat(fullscreenMedia.date, 'd M H:i:s') : '';
</script>

<div
	class={'placeholder-container' +
		' ' +
		(recording?.type === RecordingMediaType.IMAGE ? 'image' : 'video')}
>
	{#if recording?.type === RecordingMediaType.VIDEO && isHls}
		<HLSPlayer
			borderRadius="big"
			videoSrc={hlsVideoSrc}
			encrypted={!!recording.encrypted}
			keyUri={recording.keyUri ?? ''}
			deviceId={recording.mac}
			recordingId={recording.id}
			recordingDate={recording.date ?? ''}
			on:requirekey
		/>
	{:else if recording?.type === RecordingMediaType.VIDEO && videoUrl}
		<VideoPlayer borderRadius="big" videoSrc={videoUrl} />
	{:else if recording?.type === RecordingMediaType.IMAGE}
		<CameraThumbnail
			size="full"
			aspectRatio="16-9"
			deviceId={recording.mac}
			recId={recording.id}
			recDate={recording.date}
			src={recording.imageUrls?.large}
			imageSize="large"
			encrypted={!!recording.encrypted}
			on:click={handleImageClick}
		/>
	{:else}
		<CameraThumbnail size="full" aspectRatio="16-9" />
	{/if}
</div>

<Modal
	on:close={handleCloseModal}
	showModal={showFullscreenModal}
	title={formattedDate}
	backdropClose={true}
>
	<svelte:fragment slot="content">
		{#if fullscreenMedia}
			<ZoomableContainer>
				<CameraThumbnail
					size="full"
					aspectRatio="16-9"
					deviceId={fullscreenMedia.mac}
					recId={fullscreenMedia.id}
					recDate={fullscreenMedia.date}
					src={fullscreenMedia.imageUrls?.large}
					imageSize="large"
					encrypted={!!fullscreenMedia.encrypted}
				/>
			</ZoomableContainer>
		{/if}
	</svelte:fragment>
</Modal>

<style>
	.placeholder-container {
		aspect-ratio: 16/9;
		width: 100%;
		border-radius: var(--radius-big);
		overflow: hidden;
	}

	.placeholder-container.image {
		cursor: pointer;
	}
</style>
